export default function Solution() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Main Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            What is Systematic Marketing?
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            Your key to stable growth and sales
          </p>
        </div>

        {/* Problem Statement */}
        <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-3xl p-10 md:p-16 mb-12 border border-red-100">
          <div className="flex items-start gap-6 mb-6">
            <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-3xl text-white"></i>
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Marketing Chaos — Enemy of Your Profit
              </h3>
              <p className="text-lg text-gray-800 leading-relaxed">
                Most companies live in a state of <strong>"marketing chaos"</strong>: sporadic advertising launches, changing contractors, no understanding of which dollar brought profit and which was wasted.
              </p>
            </div>
          </div>
        </div>

        {/* Solution Definition */}
        <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-10 md:p-16 mb-20 border border-cyan-100">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center flex-shrink-0">
              <i className="ri-settings-3-line text-3xl text-white"></i>
            </div>
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                Systematic Marketing — the opposite of chaos
              </h3>
              <p className="text-xl text-gray-800 leading-relaxed mb-4">
                This is an approach where marketing is viewed <strong>not as a set of disparate actions</strong> (launched Direct, wrote a post), but as <strong className="text-cyan-600">a single organism working for predictable financial results</strong>.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                This is the foundation that turns clicks into real, loyal customers, and every dollar invested brings measurable profit.
              </p>
            </div>
          </div>
        </div>

        {/* Three Pillars Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-full mb-6">
            <span className="font-bold text-lg">⚡ Three Pillars of Systematic Marketing</span>
          </div>
          <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Unity of Three Elements
          </h3>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Systematic marketing rests on the synergy of three key components that work as a single mechanism
          </p>
        </div>

        {/* Three Pillars */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {/* Pillar 1: Strategy */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-cyan-100 hover:shadow-2xl hover:border-cyan-300 transition-all duration-300">
            <div className="w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <i className="ri-brain-line text-4xl text-white"></i>
            </div>
            <div className="text-center mb-6">
              <div className="inline-block px-4 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-3">
                PILLAR #1
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-2">
                Strategy
              </h4>
              <p className="text-cyan-600 font-semibold">The Brain of the System</p>
            </div>
            
            <p className="text-gray-700 mb-6 text-center leading-relaxed">
              Understanding <strong>who, where, and how</strong> we attract
            </p>

            <div className="space-y-4">
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-user-line text-cyan-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <strong className="text-slate-900 block mb-1">CJM (Customer Journey Map)</strong>
                    <p className="text-gray-600 text-sm">Customer path from need awareness to purchase</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                <div className="flex items-start gap-3">
                  <i className="ri-medal-line text-cyan-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <strong className="text-slate-900 block mb-1">USP and Positioning</strong>
                    <p className="text-gray-600 text-sm">Why we're better than competitors and why buy from us</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                <div className="flex items-start gap-3">
                  <i className="ri-money-dollar-circle-line text-cyan-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <strong className="text-slate-900 block mb-1">Financial Goals</strong>
                    <p className="text-gray-600 text-sm">Clear understanding of how many leads and at what cost we need</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pillar 2: Tools */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-blue-100 hover:shadow-2xl hover:border-blue-300 transition-all duration-300">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <i className="ri-tools-line text-4xl text-white"></i>
            </div>
            <div className="text-center mb-6">
              <div className="inline-block px-4 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-3">
                PILLAR #2
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-2">
                Tools
              </h4>
              <p className="text-blue-600 font-semibold">The Hands of the System</p>
            </div>
            
            <p className="text-gray-700 mb-6 text-center leading-relaxed">
              Technical implementation of strategy
            </p>

            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-start gap-3">
                  <i className="ri-advertisement-line text-blue-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <strong className="text-slate-900 block mb-1">Advertising Channels</strong>
                    <p className="text-gray-600 text-sm">Yandex.Direct, Google Ads, VKontakte, social media targeting</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-start gap-3">
                  <i className="ri-robot-2-line text-blue-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <strong className="text-slate-900 block mb-1">Automation</strong>
                    <p className="text-gray-600 text-sm">Services that eliminate human error and work 24/7</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-start gap-3">
                  <i className="ri-line-chart-line text-blue-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <strong className="text-slate-900 block mb-1">End-to-End Analytics</strong>
                    <p className="text-gray-600 text-sm">Links clicks to cash in the register — you see every dollar</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pillar 3: Team */}
          <div className="bg-white rounded-2xl p-8 shadow-xl border-2 border-indigo-100 hover:shadow-2xl hover:border-indigo-300 transition-all duration-300">
            <div className="w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mb-6 mx-auto">
              <i className="ri-team-line text-4xl text-white"></i>
            </div>
            <div className="text-center mb-6">
              <div className="inline-block px-4 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-semibold mb-3">
                PILLAR #3
              </div>
              <h4 className="text-2xl font-bold text-slate-900 mb-2">
                Team
              </h4>
              <p className="text-indigo-600 font-semibold">The Heart of the System</p>
            </div>
            
            <p className="text-gray-700 mb-6 text-center leading-relaxed">
              People who manage the tools
            </p>

            <div className="space-y-4">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                <div className="flex items-start gap-3">
                  <i className="ri-focus-3-line text-indigo-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <strong className="text-slate-900 block mb-1">Clear Function and KPI</strong>
                    <p className="text-gray-600 text-sm">Each employee knows their area of responsibility and goals</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                <div className="flex items-start gap-3">
                  <i className="ri-file-list-3-line text-indigo-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <strong className="text-slate-900 block mb-1">Clear Regulations</strong>
                    <p className="text-gray-600 text-sm">Instructions and processes that ensure quality</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                <div className="flex items-start gap-3">
                  <i className="ri-rocket-line text-indigo-600 text-xl flex-shrink-0 mt-1"></i>
                  <div>
                    <strong className="text-slate-900 block mb-1">Result-Oriented Motivation</strong>
                    <p className="text-gray-600 text-sm">Focus on achieving goals, not imitating the process</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-3xl p-10 md:p-16 text-white">
          <div className="text-center mb-12">
            <div className="inline-block px-6 py-2 bg-cyan-500/20 rounded-full mb-4">
              <span className="text-cyan-300 font-semibold">✅ Your Results After Implementation</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              Your Success and Profit — Not a Coincidence
            </h3>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              It's the result of smart algorithms and flawless strategy. We build a powerful, autonomous marketing department for you.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-eye-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Control and Transparency</h4>
                  <p className="text-gray-300">You always know how much profit each dollar invested in traffic brings.</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-team-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Loyalty and Community</h4>
                  <p className="text-gray-300">Social media stops being a "business card" and becomes a tool for repeat sales.</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Time Savings</h4>
                  <p className="text-gray-300">Automation frees your time for strategic management, eliminating routine.</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-trophy-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold mb-2">Dominance on Ozon</h4>
                  <p className="text-gray-300">Predictable revenue growth and bringing products to the top.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
